import React, { useEffect, useState } from 'react';
import { Phone, MapPin, Clock, Star, CheckCircle, Leaf, Scissors, Sprout, Home } from 'lucide-react';
import '../styles/LawnCare.css';

const LawnCareLanding = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      icon: <Scissors className="service-icon" />,
      title: 'Lawn Mowing',
      description: 'Professional lawn mowing with clean edges and debris removal for a pristine finish.'
    },
    {
      icon: <Sprout className="service-icon" />,
      title: 'Garden Maintenance',
      description: 'Complete garden care including pruning, weeding, and landscape upkeep.'
    },
    {
      icon: <Leaf className="service-icon" />,
      title: 'Hedge Trimming',
      description: 'Expert hedge and shrub trimming to maintain your property\'s beauty and health.'
    },
    {
      icon: <Home className="service-icon" />,
      title: 'Property Clean-Up',
      description: 'Thorough cleanup of pathways, driveways, and outdoor spaces.'
    }
  ];

  const reviews = [
    {
      name: 'Lee Beetson',
      rating: 5,
      text: 'I would highly recommend Phillip from Greenland Lawn services. He did some pruning for me today. Was extremely happy with his work plus did extra work for me with no extra charge. He arrived right on time and was very polite and friendly.',
      date: '4 months ago'
    },
    {
      name: 'Christie Greig',
      rating: 5,
      text: 'Very happy with the work we had done. Lawn transformed from a jungle and weeding to the paved area and driveway completed. Friendly and good communication. Will def call back when needed.',
      date: '2 months ago'
    },
    {
      name: 'Upama Mondal',
      rating: 5,
      text: 'Philip was incredibly responsible, polite, and did an excellent job with our gardening. He even took the initiative to complete extra tasks that were needed, without charging extra.',
      date: '10 months ago'
    },
    {
      name: 'Quentin Sauzedde',
      rating: 5,
      text: 'Quality service, really nothing to complain about! The garden maintenance was better done and cheaper than by the last gardener. Thanks a lot!',
      date: '2 years ago'
    }
  ];

  return (
    <div className="lawn-care-site">
      {/* Header */}
      <header className={`network-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-wrapper">
          <div className="network-logo">Greenland Lawn Care</div>
          <nav className="network-nav">
            <button onClick={() => scrollToSection('home')} className="network-nav-link">Home</button>
            <button onClick={() => scrollToSection('services')} className="network-nav-link">Services</button>
            <button onClick={() => scrollToSection('reviews')} className="network-nav-link">Reviews</button>
            <button onClick={() => scrollToSection('contact')} className="network-nav-link">Contact</button>
            <a href="tel:+61415740430" className="btn-header-call">
              <Phone size={18} />
              <span>0415 740 430</span>
            </a>
          </nav>
        </div>
      </header>

      {/* Mobile Sticky Call Button */}
      <a href="tel:+61415740430" className="sticky-call-btn">
        <Phone size={20} />
        <span>Call Now</span>
      </a>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <Star size={16} fill="currentColor" />
              <span>5.0 Rating • 64 Reviews</span>
            </div>
            <h1 className="display-large">
              Professional Lawn Care<br />Services in Balga
            </h1>
            <p className="body-large hero-description">
              Transform your outdoor space with expert lawn mowing, garden maintenance, and landscaping services. Quality work, honest pricing, and exceptional attention to detail.
            </p>
            <div className="hero-cta-group">
              <a href="tel:+61415740430" className="btn-cta">
                <Phone size={20} />
                Call 0415 740 430
              </a>
              <button onClick={() => scrollToSection('services')} className="btn-secondary">
                View Services
              </button>
            </div>
            <div className="hero-info">
              <div className="hero-info-item">
                <Clock size={18} />
                <span>Open 6 Days a Week</span>
              </div>
              <div className="hero-info-item">
                <MapPin size={18} />
                <span>Serving Balga & Surrounding Areas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <div className="gallery-grid">
            <div className="gallery-item">
              <img src="/images/lawn-care-1.jpg" alt="Professional lawn mowing service in Balga" />
            </div>
            <div className="gallery-item">
              <img src="/images/lawn-care-2.jpg" alt="Garden maintenance and landscaping in Perth" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Services</h2>
            <p className="body-large">Professional lawn care and garden maintenance tailored to your needs</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="network-card service-card">
                <div className="service-icon-wrapper">
                  {service.icon}
                </div>
                <h3 className="network-card-title">{service.title}</h3>
                <p className="network-card-content">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="services-cta">
            <a href="tel:+61415740430" className="btn-primary">
              Get a Quote Today
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="reviews-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">What Our Customers Say</h2>
            <div className="rating-summary">
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} fill="#D3FF62" color="#D3FF62" />
                ))}
              </div>
              <span className="rating-text">5.0 out of 5 • 64 reviews</span>
            </div>
          </div>
          <div className="reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="network-card review-card">
                <div className="review-header">
                  <div>
                    <h4 className="review-name">{review.name}</h4>
                    <span className="review-date">{review.date}</span>
                  </div>
                  <div className="review-stars">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#D3FF62" color="#D3FF62" />
                    ))}
                  </div>
                </div>
                <p className="review-text">{review.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2 className="heading-1">Get In Touch</h2>
              <p className="body-large">Ready to transform your lawn? Call us today for a quote!</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <div className="contact-label">Phone</div>
                    <a href="tel:+61415740430" className="contact-value">0415 740 430</a>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <div className="contact-label">Location</div>
                    <div className="contact-value">Camberwell Rd, Balga WA 6061</div>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <Clock size={24} />
                  </div>
                  <div>
                    <div className="contact-label">Business Hours</div>
                    <div className="contact-value">
                      Monday - Saturday: 6am - 6pm<br />
                      Sunday: 6am - 12pm
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-cta-card">
              <h3 className="heading-2">Ready to Book?</h3>
              <p className="body-medium">Call us now for a free quote and let us take care of your lawn care needs.</p>
              <a href="tel:+61415740430" className="btn-cta">
                <Phone size={20} />
                Call 0415 740 430
              </a>
              <ul className="benefits-list">
                <li><CheckCircle size={18} /> Professional & Reliable</li>
                <li><CheckCircle size={18} /> Honest Pricing</li>
                <li><CheckCircle size={18} /> Quality Guaranteed</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 className="footer-title">Greenland Lawn Care Services</h3>
              <p className="footer-text">Professional lawn care and garden maintenance in Balga and surrounding areas.</p>
              <div className="footer-rating">
                <Star size={16} fill="#D3FF62" color="#D3FF62" />
                <span>5.0 Rating • 64 Reviews</span>
              </div>
              <a href="/greenland-lawn-care-website.zip" download className="download-link">
                📦 Download Website ZIP
              </a>
            </div>

            <div className="footer-section">
              <h4 className="footer-heading">Quick Links</h4>
              <button onClick={() => scrollToSection('services')} className="footer-link">Services</button>
              <button onClick={() => scrollToSection('reviews')} className="footer-link">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="footer-link">Contact</button>
            </div>

            <div className="footer-section">
              <h4 className="footer-heading">Contact</h4>
              <a href="tel:+61415740430" className="footer-link">0415 740 430</a>
              <div className="footer-text">Camberwell Rd, Balga WA 6061</div>
              <div className="footer-text">Monday - Saturday: 6am - 6pm</div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 Greenland Lawn Care Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LawnCareLanding;